/**
 * Employee System Access Tab Component
 * 
 * Manages system access, role, permissions and terminal PIN.
 * Permissions are saved together with the main employee form.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { 
  Shield, Key, Check, Trash2, Loader2, AlertCircle, Eye, EyeOff, Copy,
  Crown, Building, MapPin, User, RotateCcw
} from 'lucide-react';
import { copyToClipboard } from './helpers';
import { usersAPI } from '../../lib/api';
import { toast } from 'sonner';

const SystemAccessTab = ({
  formData,
  handleChange,
  linkedUser,
  linkingUser,
  creatingAccess,
  handleRemoveSystemAccess,
  handleCreateSystemAccess,
  userPermissions,
  handlePermissionChange,
  handleRoleSelect,
  roles,
  branches,
  locations,
  newPassword,
  setNewPassword,
  showNewPassword,
  setShowNewPassword,
  generateNewPassword,
}) => {
  const [resetInfo, setResetInfo] = React.useState(null);
  const [savingReset, setSavingReset] = React.useState(false);
  const employeeName = `${formData?.first_name || ''} ${formData?.last_name || ''}`.trim();

  const doResetPassword = async () => {
    if (!newPassword) return;
    setSavingReset(true);
    try {
      await usersAPI.resetPassword(linkedUser.id, newPassword);
      setResetInfo({ email: linkedUser.email, password: newPassword });
      setNewPassword('');
      toast.success('Passwort zurückgesetzt');
    } catch {
      toast.error('Fehler beim Zurücksetzen');
    } finally {
      setSavingReset(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Card 1: System Access + Role (primary focus) */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Systemzugang & Rolle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {linkedUser ? (
            <div className="space-y-6">
              {/* Active access banner */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                      <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">Systemzugang aktiv</p>
                      <p className="text-sm text-green-600 dark:text-green-400">{linkedUser.email}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveSystemAccess}
                    disabled={linkingUser}
                    className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                    data-testid="remove-access-btn"
                  >
                    {linkingUser ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Entfernen
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Email mismatch warning */}
              {formData.business_email && linkedUser.email && 
               formData.business_email.toLowerCase() !== linkedUser.email.toLowerCase() && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="flex items-start gap-2 text-amber-800 dark:text-amber-200">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p className="text-sm">
                      Login-E-Mail (<span className="font-mono">{linkedUser.email}</span>) weicht von der 
                      dienstlichen E-Mail (<span className="font-mono">{formData.business_email}</span>) ab.
                      Wird beim Speichern automatisch aktualisiert.
                    </p>
                  </div>
                </div>
              )}

              {/* Role + Scope in one section */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-base font-medium">Rolle</Label>
                  <Select 
                    value={userPermissions.role_id || ''} 
                    onValueChange={(v) => handleRoleSelect(v, [formData.location_id, ...(formData.additional_location_ids || [])].filter(Boolean))}
                  >
                    <SelectTrigger className="w-full max-w-md" data-testid="role-select">
                      <SelectValue placeholder="Rolle auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: role.color || '#6B7280' }}
                            />
                            {role.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Die Rolle bestimmt die Systemrolle und Zugriffsrechte. Wird automatisch beim Speichern des Mitarbeiters übernommen.
                  </p>
                </div>

                {/* Scope */}
                <div className="space-y-2">
                  <Label className="font-medium">Geltungsbereich</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { value: 'all', label: 'Alle Bereiche', icon: Crown, desc: 'Admin' },
                      { value: 'branch', label: 'Niederlassung', icon: Building, desc: 'NL-Leiter' },
                      { value: 'location', label: 'Standort', icon: MapPin, desc: 'Standortleiter' },
                      { value: 'own', label: 'Eigene Daten', icon: User, desc: 'Mitarbeiter' },
                    ].map((scope) => (
                      <div
                        key={scope.value}
                        onClick={() => handlePermissionChange('scope_type', scope.value)}
                        data-testid={`scope-${scope.value}`}
                        className={`cursor-pointer p-3 rounded-lg border-2 transition-all text-center ${
                          userPermissions.scope_type === scope.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <scope.icon className={`h-5 w-5 mx-auto mb-1 ${userPermissions.scope_type === scope.value ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className="text-sm font-medium block">{scope.label}</span>
                        <span className="text-[10px] text-muted-foreground">{scope.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Branch selection */}
                {userPermissions.scope_type === 'branch' && (
                  <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                    <Label>Niederlassungen</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {branches.map((branch) => (
                        <div key={branch.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`branch-${branch.id}`}
                            checked={userPermissions.branch_ids.includes(branch.id)}
                            onCheckedChange={(checked) => {
                              const ids = checked 
                                ? [...userPermissions.branch_ids, branch.id]
                                : userPermissions.branch_ids.filter(id => id !== branch.id);
                              handlePermissionChange('branch_ids', ids);
                            }}
                          />
                          <label htmlFor={`branch-${branch.id}`} className="text-sm cursor-pointer">{branch.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location selection */}
                {userPermissions.scope_type === 'location' && (
                  <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label>Standorte</Label>
                      {formData.location_id && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs rounded-full"
                          data-testid="use-own-location-btn"
                          onClick={() => {
                            const own = [formData.location_id, ...(formData.additional_location_ids || [])].filter(Boolean);
                            const merged = Array.from(new Set([...(userPermissions.location_ids || []), ...own]));
                            handlePermissionChange('location_ids', merged);
                          }}
                        >
                          <MapPin className="h-3 w-3 mr-1" /> Arbeitsort übernehmen
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Für Haustechniker/Hausmeister: die zuständigen Standorte wählen – so sieht die Person genau die Aufträge dieser Standorte.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {locations.map((loc) => (
                        <div key={loc.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`loc-${loc.id}`}
                            checked={userPermissions.location_ids.includes(loc.id)}
                            onCheckedChange={(checked) => {
                              const ids = checked
                                ? [...userPermissions.location_ids, loc.id]
                                : userPermissions.location_ids.filter(id => id !== loc.id);
                              handlePermissionChange('location_ids', ids);
                            }}
                          />
                          <label htmlFor={`loc-${loc.id}`} className="text-sm cursor-pointer">{loc.name}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* No system access - Create flow */
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Kein Systemzugang</p>
                    <p className="text-sm">Dieser Mitarbeiter hat noch keinen Zugang zum System.</p>
                  </div>
                </div>
              </div>

              {formData.business_email ? (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                  <div>
                    <Label className="text-base font-medium">Systemzugang erstellen</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Login-E-Mail: <span className="font-mono font-medium">{formData.business_email}</span>
                    </p>
                  </div>

                  {/* Role for new access */}
                  <div className="space-y-2">
                    <Label>Rolle</Label>
                    <Select 
                      value={userPermissions.role_id || ''} 
                      onValueChange={(v) => handleRoleSelect(v, [formData.location_id, ...(formData.additional_location_ids || [])].filter(Boolean))}
                    >
                      <SelectTrigger className="w-full max-w-md" data-testid="new-role-select">
                        <SelectValue placeholder="Rolle auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: role.color || '#6B7280' }}
                              />
                              {role.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Password */}
                  <div className="space-y-2">
                    <Label>Passwort</Label>
                    <div className="flex gap-2 max-w-md">
                      <div className="relative flex-1">
                        <Input
                          type={showNewPassword ? 'text' : 'password'}
                          placeholder="Manuell eingeben oder automatisch generieren"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          data-testid="new-password-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                      <Button type="button" variant="outline" onClick={generateNewPassword}>
                        Generieren
                      </Button>
                    </div>
                    {newPassword && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(newPassword, toast)}
                        className="text-xs"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Passwort kopieren
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Ohne Eingabe wird ein sicheres Passwort automatisch generiert. Der Mitarbeiter muss beim ersten Login ein neues Passwort und einen Zeiterfassungs-PIN festlegen.
                    </p>
                  </div>

                  <Button
                    type="button"
                    onClick={handleCreateSystemAccess}
                    disabled={creatingAccess}
                    className="w-full max-w-md"
                    data-testid="create-access-btn"
                  >
                    {creatingAccess ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Erstelle Zugang...
                      </>
                    ) : (
                      <>
                        <Key className="mr-2 h-4 w-4" />
                        Systemzugang erstellen
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="p-4 border border-dashed rounded-lg text-center">
                  <p className="text-muted-foreground">
                    Bitte zuerst eine <strong>dienstliche E-Mail-Adresse</strong> im Reiter "Beschäftigung" eintragen.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card 2: Password Reset (only when access exists) */}
      {linkedUser && (
        <Card className="border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading flex items-center gap-2 text-base">
              <RotateCcw className="h-4 w-4" />
              Passwort zurücksetzen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 max-w-md">
              <div className="relative flex-1">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Neues Passwort"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="reset-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-8 top-0 h-full"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <Button type="button" variant="outline" onClick={generateNewPassword}>
                Generieren
              </Button>
              <Button 
                type="button"
                data-testid="reset-password-btn"
                onClick={doResetPassword}
                disabled={!newPassword || savingReset}
              >
                {savingReset ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Zurücksetzen'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card 3: Terminal PIN */}
      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2 text-base">
            <Key className="h-4 w-4" />
            Terminal-PIN
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            4-6 stellige PIN für die Zeiterfassung am Terminal/Kiosk.
          </p>
          <div className="max-w-xs">
            <Input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="••••"
              value={formData.pin || ''}
              onChange={(e) => handleChange('pin', e.target.value.replace(/\D/g, ''))}
              data-testid="employee-pin"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bestätigung: Passwort zurückgesetzt – zeigt neues Passwort zum Kopieren */}
      <Dialog open={!!resetInfo} onOpenChange={(o) => { if (!o) setResetInfo(null); }}>
        <DialogContent data-testid="reset-password-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Check className="h-5 w-5" /> Passwort zurückgesetzt
            </DialogTitle>
            <DialogDescription>
              Das Passwort {employeeName ? `von ${employeeName}` : ''} wurde erfolgreich zurückgesetzt.
              Bitte die Zugangsdaten sicher an die Person übergeben – sie werden nach dem Schließen nicht erneut angezeigt.
            </DialogDescription>
          </DialogHeader>
          {resetInfo && (
            <div className="space-y-3 py-1">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Login-E-Mail</Label>
                <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                  <span className="flex-1 font-mono text-sm break-all" data-testid="reset-confirm-email">{resetInfo.email}</span>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(resetInfo.email, toast)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Neues Passwort</Label>
                <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                  <span className="flex-1 font-mono text-sm break-all" data-testid="reset-confirm-password">{resetInfo.password}</span>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(resetInfo.password, toast)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                data-testid="reset-confirm-copy-both"
                onClick={() => copyToClipboard(`Login: ${resetInfo.email}\nPasswort: ${resetInfo.password}`, toast)}
              >
                <Copy className="h-4 w-4 mr-2" /> Beides kopieren
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button type="button" className="rounded-full" onClick={() => setResetInfo(null)} data-testid="reset-confirm-close">
              Fertig
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemAccessTab;
